import { useState } from "react"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { X, Plus } from "lucide-react"
import { type MeasureInfo, type ActionCategory } from "../app/services/simulationCreatorApi"

type ActionMeasureItemProps = {
  action: ActionCategory
  actionPath: string
  measures: MeasureInfo[]
  onAddMeasure: (actionPath: string, measureId: number) => void
  onRemoveMeasure: (actionPath: string, measureId: number) => void
  isLeaf: boolean
}

export const ActionMeasureItem = ({
  action,
  actionPath,
  measures,
  onAddMeasure,
  onRemoveMeasure,
  isLeaf
}: ActionMeasureItemProps) => {
  const [showMeasurePicker, setShowMeasurePicker] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Get measure details for assigned IDs
  const assignedMeasures = (action.measureIds || [])
    .map(id => measures.find(m => m.id === id))
    .filter((m): m is MeasureInfo => m !== undefined)

  // Available measures (not already assigned)
  const availableMeasures = measures.filter(
    m => !(action.measureIds || []).includes(m.id)
  )

  // Filter available measures by search
  const filteredMeasures = searchTerm
    ? availableMeasures.filter(m =>
        m.measureName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.description && m.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : availableMeasures

  return (
    <div className={`py-2 px-3 rounded ${isLeaf ? "bg-gray-50" : ""}`}>
      <div className="flex flex-col gap-1">
        {/* Action Text */}
        <div>
          <span className={`${isLeaf ? "font-medium text-gray-900" : "text-gray-600"} break-words`}>
            {action.text}
          </span>
          {isLeaf && (
            <Badge variant="outline" className="ml-2 text-xs">
              Leaf
            </Badge>
          )}
        </div>

        {/* Measure Pills (only for leaf actions) */}
        {isLeaf && (
          <div className="flex flex-wrap gap-1 items-center">
            {assignedMeasures.map(measure => (
              <Badge
                key={measure.id}
                variant="secondary"
                className="flex items-center gap-1 pr-1"
              >
                <span className="max-w-32 truncate">{measure.measureName}</span>
                <button
                  onClick={() => onRemoveMeasure(actionPath, measure.id)}
                  className="ml-1 hover:text-red-500 transition-colors"
                  title="Remove measure"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {/* Add Measure Dropdown */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setShowMeasurePicker(!showMeasurePicker)}
                title="Add measure"
              >
                <Plus className="h-4 w-4" />
              </Button>

              {showMeasurePicker && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setShowMeasurePicker(false)
                      setSearchTerm("")
                    }}
                  />

                  {/* Dropdown */}
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white border rounded-lg shadow-lg z-20 p-2">
                    {/* Search input */}
                    <input
                      type="text"
                      placeholder="Search measures..."
                      className="w-full px-2 py-1 text-sm border rounded mb-2"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoFocus
                    />

                    {/* Measure list */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredMeasures.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-2">
                          {availableMeasures.length === 0
                            ? "All measures assigned"
                            : "No matches found"}
                        </div>
                      ) : (
                        filteredMeasures.map(measure => (
                          <button
                            key={measure.id}
                            onClick={() => {
                              onAddMeasure(actionPath, measure.id)
                              setShowMeasurePicker(false)
                              setSearchTerm("")
                            }}
                            className="w-full text-left px-2 py-1.5 hover:bg-gray-100 rounded text-sm"
                          >
                            <div className="font-medium truncate">{measure.measureName}</div>
                            {measure.description && (
                              <div className="text-xs text-gray-500 truncate">
                                {measure.description.substring(0, 60)}...
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ActionMeasureItem
